import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import { HeaderButton } from '../../../EmployeesRepository/EmployeeForm/styles';
import PositionSelector from '../../../common/PositionSelector';
import { organizationApi } from '../../../../services/api';
import { useAlert } from '../../../../contexts/AlertContext';
import { permissionsApi } from '../../../../services/api/permissionsApi';
import { COLORS } from '../../../../constants/radar';

const STEPS = ['Select Position', 'Select Grades'];

const AddPositionGradesWizard = ({
  open,
  onClose,
  roleId,
  existingPositionIds = [],
  onSuccess,
  // Edit mode props
  editMode = false,
  positionMapping = null
}) => {
  const { showAlert } = useAlert();
  const [activeStep, setActiveStep] = useState(editMode ? 1 : 0);
  const [selectedPositionId, setSelectedPositionId] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [allGrades, setAllGrades] = useState([]);
  const [assignedGrades, setAssignedGrades] = useState([]);
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [isLoadingGrades, setIsLoadingGrades] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [removingGradeId, setRemovingGradeId] = useState(null);

  useEffect(() => {
    if (open) {
      fetchGrades();
      if (editMode && positionMapping) {
        // In edit mode, fetch assigned grades for the position
        fetchAssignedGrades();
      }
    }
  }, [open, editMode, positionMapping?.id]);

  useEffect(() => {
    if (!open) {
      // Reset state when modal closes
      setActiveStep(editMode ? 1 : 0);
      setSelectedPositionId('');
      setSelectedPosition(null);
      setAssignedGrades([]);
      setSelectedGradeId('');
    }
  }, [open, editMode]);

  const fetchGrades = async () => {
    try {
      setIsLoadingGrades(true);
      const gradesData = await organizationApi.getAllGrades();
      setAllGrades(gradesData);
    } catch (error) {
      showAlert('Failed to load grades', 'error', error);
    } finally {
      setIsLoadingGrades(false);
    }
  };

  const fetchAssignedGrades = async () => {
    if (!positionMapping?.id) return;
    try {
      setIsLoadingGrades(true);
      const grades = await permissionsApi.getRolePositionMappingGrades(positionMapping.id);
      setAssignedGrades(grades);
    } catch (error) {
      console.error('Error fetching assigned grades:', error);
      setAssignedGrades([]);
    } finally {
      setIsLoadingGrades(false);
    }
  };

  const handlePositionChange = (positionId) => {
    setSelectedPositionId(positionId);
  };

  const handleSelectedPositionChange = (position) => {
    setSelectedPosition(position);
  };

  const handleAddGrade = async () => {
    if (!selectedGradeId) return;

    const mappingId = editMode ? positionMapping?.id : null;

    if (editMode && mappingId) {
      // In edit mode, save immediately to API
      try {
        setIsAdding(true);
        const newGradeMapping = await permissionsApi.createRolePositionMappingGrade({
          rolePositionMappingId: mappingId,
          gradeId: selectedGradeId
        });
        setAssignedGrades(prev => [...prev, newGradeMapping]);
        setSelectedGradeId('');
        showAlert('Grade added successfully', 'success');
        onSuccess?.();
      } catch (error) {
        console.error('Error adding grade:', error);
        showAlert(error.message || 'Failed to add grade', 'error');
      } finally {
        setIsAdding(false);
      }
    } else {
      // In add mode, just update local state
      const gradeToAdd = allGrades.find(g => g.id === selectedGradeId);
      if (gradeToAdd) {
        setAssignedGrades(prev => [...prev, {
          id: `temp-${Date.now()}`,
          gradeId: selectedGradeId,
          grade: gradeToAdd
        }]);
        setSelectedGradeId('');
      }
    }
  };

  const handleRemoveGrade = async (gradeMapping) => {
    if (editMode && positionMapping?.id) {
      // In edit mode, delete from API
      try {
        setRemovingGradeId(gradeMapping.id);
        await permissionsApi.deleteRolePositionMappingGrade(gradeMapping.id);
        setAssignedGrades(prev => prev.filter(g => g.id !== gradeMapping.id));
        showAlert('Grade removed successfully', 'success');
        onSuccess?.();
      } catch (error) {
        console.error('Error removing grade:', error);
        showAlert(error.message || 'Failed to remove grade', 'error');
      } finally {
        setRemovingGradeId(null);
      }
    } else {
      // In add mode, just update local state
      setAssignedGrades(prev => prev.filter(g => g.id !== gradeMapping.id));
    }
  };

  const handleNext = () => {
    if (activeStep === STEPS.length - 1) {
      handleSubmit();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0 || editMode) {
      onClose();
    } else {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (editMode) {
      // In edit mode, just close - changes are already saved
      onClose();
      return;
    }

    if (!selectedPositionId || !roleId) {
      showAlert('Please select a position', 'warning');
      return;
    }

    try {
      setIsSubmitting(true);

      // Step 1: Assign position to role
      const rolePositionMapping = await permissionsApi.assignPositionToRole({
        roleId,
        positionId: selectedPositionId
      });

      // Step 2: If position requires grades and grades are selected, create grade associations
      if (selectedPosition?.requiresGrade && assignedGrades.length > 0) {
        const gradePromises = assignedGrades.map(gradeMapping =>
          permissionsApi.createRolePositionMappingGrade({
            rolePositionMappingId: rolePositionMapping.id,
            gradeId: gradeMapping.gradeId || gradeMapping.grade?.id
          })
        );
        await Promise.all(gradePromises);
      }

      showAlert('Position and grades added successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error adding position:', error);
      showAlert(error.message || 'Failed to add position', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isNextDisabled = () => {
    if (editMode) return false;

    switch (activeStep) {
      case 0:
        return !selectedPositionId || existingPositionIds.includes(selectedPositionId);
      case 1:
        return isSubmitting;
      default:
        return false;
    }
  };

  const isPositionAlreadyAssigned = selectedPositionId && existingPositionIds.includes(selectedPositionId);

  // Filter out already assigned grades from the dropdown
  const availableGrades = allGrades.filter(
    grade => !assignedGrades.some(assigned =>
      (assigned.gradeId === grade.id) || (assigned.grade?.id === grade.id)
    )
  );

  // Get position info for display
  const currentPosition = editMode
    ? positionMapping?.position
    : selectedPosition;
  const currentPositionLabel = currentPosition?.label || positionMapping?.label || 'Position';
  const requiresGrade = currentPosition?.requiresGrade ?? true;

  const renderStepContent = (step) => {
    // In edit mode, always show step 1 content (grades)
    if (editMode || step === 1) {
      return (
        <Box sx={{ p: 2 }}>
          {/* Position Info - Compact inline display */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Position:
            </Typography>
            <Chip
              label={currentPositionLabel}
              size="small"
              sx={{ backgroundColor: '#e2e8f0', fontWeight: 500 }}
            />
          </Box>

          {requiresGrade ? (
            <>
              {isLoadingGrades ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Add Grade Section - Inline with selector */}
                  {availableGrades.length === 0 ? (
                    <Box sx={{ mb: 3 }}>
                      <Alert severity="info" sx={{ py: 1 }}>
                        All available grades have been assigned.
                      </Alert>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Add grade</InputLabel>
                        <Select
                          value={selectedGradeId}
                          onChange={(e) => setSelectedGradeId(e.target.value)}
                          label="Add grade"
                          disabled={isAdding}
                        >
                          {availableGrades.map(grade => (
                            <MenuItem key={grade.id} value={grade.id}>
                              {grade.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <HeaderButton
                        onClick={handleAddGrade}
                        disabled={!selectedGradeId || isAdding}
                        style={{ opacity: (!selectedGradeId || isAdding) ? 0.5 : 1, cursor: (!selectedGradeId || isAdding) ? 'not-allowed' : 'pointer' }}
                      >
                        {isAdding ? <CircularProgress size={14} color="inherit" /> : <FaPlus size={12} />}
                        {isAdding ? 'Adding...' : 'Add'}
                      </HeaderButton>
                    </Box>
                  )}

                  {/* Assigned Grades Section */}
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#f8fafc',
                      borderRadius: 1,
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Assigned grades ({assignedGrades.length})
                    </Typography>

                    {assignedGrades.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No grades assigned yet. Use the dropdown above to add grades.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[...assignedGrades]
                          .sort((a, b) => (a.grade?.order || 0) - (b.grade?.order || 0))
                          .map(gradeMapping => (
                          <Chip
                            key={gradeMapping.id}
                            label={gradeMapping.grade?.label || 'Unknown Grade'}
                            size="small"
                            onDelete={
                              removingGradeId === gradeMapping.id
                                ? undefined
                                : () => handleRemoveGrade(gradeMapping)
                            }
                            disabled={removingGradeId === gradeMapping.id}
                            sx={{
                              backgroundColor: COLORS.PRIMARY,
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                                '&:hover': {
                                  color: '#fecaca'
                                }
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This position does not require grades. Click "{editMode ? 'Close' : 'Add Position & Grades'}" to complete.
              </Typography>
            </Alert>
          )}
        </Box>
      );
    }

    // Step 0: Select Position
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Select the organizational position you want to attach to this role.
        </Typography>
        <PositionSelector
          value={selectedPositionId}
          onChange={handlePositionChange}
          onSelectedPositionChange={handleSelectedPositionChange}
          label="Select Position"
          fullWidth
        />
        {isPositionAlreadyAssigned && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This position is already assigned to this role.
          </Alert>
        )}
        {selectedPosition && !isPositionAlreadyAssigned && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {selectedPosition.requiresGrade
              ? 'This position supports grade assignments'
              : 'This position does not use grades'}
          </Typography>
        )}
      </Box>
    );
  };

  const dialogTitle = editMode ? 'Manage Position Grades' : 'Add Position & Grades';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ color: COLORS.PRIMARY, fontWeight: 600 }}>
        {dialogTitle}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', pt: 1 }}>
          {!editMode && (
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Box sx={{ minHeight: 300 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleBack}>
          {editMode ? 'Close' : (activeStep === 0 ? 'Cancel' : 'Back')}
        </Button>
        {!editMode && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isNextDisabled()}
            sx={{
              backgroundColor: COLORS.PRIMARY,
              '&:hover': {
                backgroundColor: '#1d4ed8'
              }
            }}
          >
            {activeStep === STEPS.length - 1 ? (
              isSubmitting ? 'Adding...' : 'Add Position & Grades'
            ) : (
              'Next'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddPositionGradesWizard;
